import { ShieldCheck, CalendarClock, BookOpen } from "lucide-react";
import { track } from "./analytics";

/**
 * The demo's conversion surface (Scope A6). Three actions:
 *   - "Start free with a BAA" → the real free signup (the only outbound prod link).
 *   - "Book a live walkthrough" → the founder's Cal.com booking page.
 *   - "Learn more" → the tutorials, closing the demo → tutorials loop (tertiary).
 * Reused by the persistent footer AND the end-of-walkthrough card.
 */
export function CtaButtons({ size = "md" }: { size?: "md" | "lg" }) {
  const pad = size === "lg" ? "px-6 py-3.5 text-[12px]" : "px-5 py-2.5 text-[11px]";
  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <a
        href="https://psy180.com/register/free"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("cta_clicked", { cta: "start_free" })}
        className={`inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] font-mono uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)] ${pad}`}
      >
        <ShieldCheck className="h-4 w-4" /> Start free with a BAA
      </a>
      <a
        href="https://cal.com/psy180/15min"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("cta_clicked", { cta: "book_walkthrough" })}
        className={`inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-navy-mid)] font-mono uppercase tracking-wide text-[var(--color-text-primary)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)] ${pad}`}
      >
        <CalendarClock className="h-4 w-4" /> Book a live walkthrough
      </a>
      <a
        href="https://psy180.com/learn"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track("cta_clicked", { cta: "learn_more" })}
        className={`inline-flex items-center gap-2 rounded-lg font-mono uppercase tracking-wide text-[var(--color-text-muted)] underline decoration-[var(--color-text-ghost)] underline-offset-4 transition-colors hover:text-[var(--color-gold-soft)] hover:decoration-[var(--color-gold-soft)] ${pad}`}
      >
        <BookOpen className="h-4 w-4" /> Learn more
      </a>
    </div>
  );
}

/** Persistent footer band shown under every demo page. */
export function DemoCtaFooter() {
  return (
    <footer className="mt-12 border-t border-[var(--color-border)] bg-[var(--color-navy-mid)]/40">
      <div className="mx-auto max-w-4xl px-4 py-10 text-center">
        <h2 className="font-heading text-2xl font-medium text-[var(--color-text-primary)]">Ready to try it with a real client?</h2>
        <p className="mx-auto mt-2 max-w-xl text-[14px] leading-relaxed text-[var(--color-text-muted)]">
          Bilateral stimulation is free during sessions, and a BAA is signed at sign-up — even on the free tier. Or have
          us walk you through it live.
        </p>
        <div className="mt-5">
          <CtaButtons size="lg" />
        </div>
        <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--color-text-ghost)]">
          Demo — sample data only · nothing here is stored
        </p>
      </div>
    </footer>
  );
}
